/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import IPrintModule, {IProjectionMatrices} from "./scene_print_interface";
import {Messenger, RenderDocument} from "js_renderer";
import {vec2, vec3, mat4} from "gl-matrix";
import Scene from "../../scene";
import {EBackgroundImageFitType, EBackgroundType} from "../background/scene_background_enums";

interface IPrintSettings {
    pdfName: string;
    numPartX: number;
    numPartY: number;
    requestedWidth: number;
    requestedHeight: number;
}

export enum EPrintStatus {
    READY,
    PROGRESS
}

export default
class PrintModule extends AbstractModule implements IPrintModule {

    protected static _typeName: string = "print";

    private _printSettings: IPrintSettings;
    private _printStatus: EPrintStatus;
    private _wasBackgroundImageEnabled: boolean = false;

    private _projectionMatricesArray: IProjectionMatrices[];
    private _runningIndex: number;

    private _logoSavedState: number;

    private _pdfDocument: any;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = PrintModule._typeName;

        // saves projection matrices
        this._projectionMatricesArray = [];
        this._runningIndex = 0;

        this._printSettings = {
            pdfName: "",
            numPartX: 0,
            numPartY: 0,
            requestedWidth: 0,
            requestedHeight: 0
        };

        this._logoSavedState = null;
        this._printStatus = EPrintStatus.READY;
    }

    public static getClassType(): string {
        return PrintModule._typeName;
    }

    /**
     *  Callback before drawing
     *  projectionMatrix - shifted camera matrix
     *  scheduleRedraw   - preparation for view redraw
     */
    public tick() {

        if (this._printStatus === EPrintStatus.READY) {
            return;
        }

        if (this._printStatus === EPrintStatus.PROGRESS) {

            // only for the last tile enable the logo (with correct scale)
            if (this.testLastTileRemaining()) {

                let printCanvas = this.canvas.getHTMLCanvas();

                // force logo scale as it is computed from defaultScale alias
                // scale type (small, normal, large) or from resolution (small, normal) for one render pass
                this.sceneAPI.logoModule.forceTemporaryScale(
                    [printCanvas.width, printCanvas.height],
                    [this._printSettings.numPartX, this._printSettings.numPartY]
                );
                this.sceneAPI.logoModule.setEnabled(true);
            }

            let camera = this.sceneAPI.viewControlsModule.getCamera();
            camera.setUserDefinedProjectionMatrices(this._projectionMatricesArray[this._runningIndex]);

            this.sceneAPI.viewModeModule.scheduleRedraw();
        }
    }

    /**
     *  Callback after drawing
     *  If there is still next tile -> proceed
     *  If the last tile was drawn  -> turn off _tilesRendering flag, schedule Redraw
     *                                 and set projection matrix into initial state
     *  shift() - whole array is shifted to the left by 1 and 1st element is deleted
     */
    public postRenderTick() {

        if (this._printStatus === EPrintStatus.READY) {
            return;
        }

        if (this._printStatus === EPrintStatus.PROGRESS) {
            this.adImageToPDfDocument(this.canvas.getHTMLCanvas().toDataURL("image/png"));
            this._runningIndex++;

            this.notifyPrintProgress();
        }

        if (this.testLAllTilesDone()) {
            this.finishPrint();
        }
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    /**
     * Start the printing (sets new projection matrices for given camera and rectangle and also sets count of tiles in x and y axis.)
     * @param aName - name
     * @param aRequestedWidth
     * @param aRequestedHeight
     *
     */
    public print(aSettings: any) {
        this._logoSavedState = this.sceneAPI.logoModule.getActiveLogo();

        this.sceneAPI.hudModule.pushSettings();
        this.sceneAPI.hudModule.set({
            compass: {
                enabled: false
            },
            axesCross: {
                enabled: false
            }
        });

        // hide bbox as well?
        this.sceneAPI.renderSettingsModule.pushSettings();
        this.sceneAPI.renderSettingsModule.set({
            geometry: {
                boundingBox: false
            }
        });

        let printCanvas = this.canvas.getHTMLCanvas();
        let nCamera = this.sceneAPI.viewControlsModule.getCamera();

        this._printSettings.pdfName = aSettings.name;
        this._printSettings.requestedWidth = aSettings.requestedWidth;
        this._printSettings.requestedHeight = aSettings.requestedHeight;

        let numPartX = 1;
        let numPartY = 1;

        if (this._printSettings.requestedHeight > printCanvas.height) {
            numPartY = Math.ceil(this._printSettings.requestedHeight / printCanvas.height);
        }
        if (this._printSettings.requestedWidth > printCanvas.width) {
            numPartX = Math.ceil(this._printSettings.requestedWidth / printCanvas.width);
        }

        let ratio = printCanvas.width / printCanvas.height;
        let newCam = nCamera.clone();
        let near = newCam.znear;
        let far = newCam.zfar;

        let sizeY;
        let sizeX;

        // for perspective projection
        let heightRatio = Math.tan(newCam.fieldOfView / 2.0);
        let widthRatio = heightRatio * ratio;
        // end perspective

        // for ortho projection
        // Copy of ortho compute from Camera.js
        let dist = vec3.distance(newCam.target, newCam.eye);
        let oppositeToCamera = Math.tan(newCam.fieldOfView / 2.0) * dist;
        sizeY = oppositeToCamera * 2.0;
        sizeX = oppositeToCamera * 2.0 * newCam.aspectRatio;

        this._projectionMatricesArray = [];

        /** At this moment print does not care about dpi */
        // heightRatio = this._targetHeight * 25.4 / 300;
        // widthRatio = this._targetWidth * 25.4 / 300;
        // end ortho

        for (let i: number = 0; i < (numPartX * numPartY); i++) {

            let newCamMatrices: IProjectionMatrices = {
                perspective: mat4.create(),
                ortho: mat4.create()
            };

            // compute perspective matrix
            {
                let incX = 1 / numPartX;
                let incY = 1 / numPartY;
                let left = 0;
                let top = 0;

                left = -1 + (i % numPartX) * 2 * incX;
                top = 1 - Math.floor(i / numPartX) * 2 * incY;

                let right = left + 2 * incX;
                let bottom = top - 2 * incY;

                left *= widthRatio * near;
                right *= widthRatio * near;
                bottom *= heightRatio * near;
                top *= heightRatio * near;

                mat4.frustum(newCamMatrices.perspective, left, right, bottom, top, near, far);
            }

            // compute orthographic matrix
            {
                let incX = (sizeX / 2) / numPartX;
                let incY = (sizeY / 2) / numPartY;
                let left = 0;
                let top = 0;

                left = (-sizeX / 2) + (i % numPartX) * 2 * incX;
                top = (sizeY / 2) - Math.floor(i / numPartX) * 2 * incY;

                let right = left + 2 * incX;
                let bottom = top - 2 * incY;

                mat4.ortho(newCamMatrices.ortho, left, right, bottom, top, near, far);
            }

            this._projectionMatricesArray.push(newCamMatrices);
        }
        this._printSettings.numPartX = numPartX;
        this._printSettings.numPartY = numPartY;

        this._runningIndex = 0;

        this.createPdfDocument();

        // first stage, second stage continues when background image is loaded
        this.writeBackgroundToPdfDocument();
    }

    private testLastTileRemaining(): boolean {
        return (this._runningIndex + 1) === this._projectionMatricesArray.length;
    }

    private testLAllTilesDone(): boolean {
        return this._runningIndex >= this._projectionMatricesArray.length;
    }

    private notifyPrintProgress() {
        Messenger.fireCDWKEvent(
            this.sceneAPI.getTargetTag(),
            "cwx3d_printProgress",
            {progress: this._runningIndex / (this._printSettings.numPartY * this._printSettings.numPartX) * 100}
        );
    }

    /**
     *  Function draws tiles into canvas and downloads result as PDF
     */
    private createPdfDocument() {

        let printCanvas = this.canvas.getHTMLCanvas();

        let pdfDocWidth = printCanvas.width * this._printSettings.numPartX;
        let pdfDocHeight = printCanvas.height * this._printSettings.numPartY;
        /**
         *  @arg l - landscape
         *  @arg p - portrait
         *  @arg pt - points ( or mm for millimeters)
         */
        if (pdfDocWidth >= pdfDocHeight) {
            // @ts-ignore
            this._pdfDocument = new jsPDF('l', 'pt', [pdfDocWidth, pdfDocHeight]);
        } else {
            // @ts-ignore
            this._pdfDocument = new jsPDF('p', 'pt', [pdfDocWidth, pdfDocHeight]);
        }
    }

    private adImageToPDfDocument(imageData: string) {

        let printCanvas = this.canvas.getHTMLCanvas();

        let startX = (this._runningIndex % this._printSettings.numPartX) * printCanvas.width;
        let startY = Math.floor(this._runningIndex / this._printSettings.numPartX) * printCanvas.height;

        this._pdfDocument.addImage(
            imageData,
            'PNG',
            startX,
            startY,
            printCanvas.width,
            printCanvas.height
        );
    }

    private finishPrint() {
        let camera = this.sceneAPI.viewControlsModule.getCamera();
        camera.setUserDefinedProjectionMatrices(null);
        this.sceneAPI.viewModeModule.scheduleRedraw();

        this.sceneAPI.logoModule.set({scaleX: 1.0, scaleY: 1.0});
        this.sceneAPI.hudModule.popSettings();
        this.sceneAPI.renderSettingsModule.popSettings();

        // reset background image to previous settings
        // this.sceneAPI.backgroundModule.setBackgroundImageOffsetAndSize(0, 0, 1, 1);
        this.sceneAPI.backgroundModule.enableBackgroundImage(this._wasBackgroundImageEnabled);
        this._wasBackgroundImageEnabled = false;
        this.sceneAPI.backgroundModule.setTransparentBackground(false);

        // save pdf document
        this._pdfDocument.save(this._printSettings.pdfName + '.pdf');

        this._printStatus = EPrintStatus.READY;

        Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_printFinish", {});
    }

    private startScenePrinting() {
        this._wasBackgroundImageEnabled = this.sceneAPI.backgroundModule.isBackgroundImageEnabled();
        this.sceneAPI.backgroundModule.enableBackgroundImage(false);

        // init the print
        this._printStatus = EPrintStatus.PROGRESS;
        this.sceneAPI.logoModule.setEnabled(false);
        this.sceneAPI.backgroundModule.setTransparentBackground(true);

        Messenger.fireCDWKEvent(
            this.sceneAPI.getTargetTag(),
            "cwx3d_printStart", {}
        );

        this.notifyPrintProgress();
    }

    private writeBackgroundToPdfDocument() {

        let printCanvas = this.canvas.getHTMLCanvas();

        let pdfDocWidth = printCanvas.width * this._printSettings.numPartX;
        let pdfDocHeight = printCanvas.height * this._printSettings.numPartY;

        let bgColor = this.sceneAPI.backgroundModule.getBackgroundColor();
        this._pdfDocument.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        this._pdfDocument.rect(0, 0, pdfDocWidth, pdfDocHeight, 'F');

        let img = new Image();
        img.onload = () => {

            let ratioWidth = pdfDocWidth / img.width;
            let ratioHeight = pdfDocHeight / img.height;
            let ratio = 1;
            if (this.sceneAPI.backgroundModule.getBackgroundImageScaleFit() === EBackgroundImageFitType.FILL) {
                ratio = Math.max(ratioWidth, ratioHeight);
            } else {
                ratio = Math.min(ratioWidth, ratioHeight);
            }

            this._pdfDocument.addImage(
                img,
                'JPEG',
                (pdfDocWidth - (img.width * ratio)) / 2.0,
                (pdfDocHeight - (img.height * ratio)) / 2.0,
                (img.width * ratio),
                (img.height * ratio),
            );

            // background image is already here
            this.startScenePrinting();
        };

        let backgroundModule = this.sceneAPI.backgroundModule;
        let imageData = backgroundModule.getBackgroundImageSource();
        if (imageData && (backgroundModule.getBackgroundType() === EBackgroundType.IMAGE)) {
            img.src = imageData;
        }
        else {
            // we do not have background image
            this.startScenePrinting();
        }
    }
}

Scene.registerSceneAPIModule(PrintModule);
